import React, { useCallback, useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import axios from '../api';
import {
  Box, Card, Chip, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel,
  TextField, Snackbar, Alert,
} from '@mui/material';
import { Event as EventIcon } from '@mui/icons-material';
import LoadingAnimation from '../components/LoadingAnimation';

type BookingStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
const STATUS_META: Record<BookingStatus, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
  pending: { label: 'Pending', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
};
const statusMeta = (status?: string) =>
  STATUS_META[status as BookingStatus] || { label: status || 'Unknown', color: 'default' as const };

interface BanquetBookingRow {
  _id: string;
  banquetHall?: { name?: string };
  customer?: { name?: string; email?: string; phone?: string };
  eventDate: string;
  timeSlot: string;
  guestCount: number;
  eventType?: string;
  contactName?: string;
  contactPhone?: string;
  specialRequests?: string;
  totalAmount: number;
  status: BookingStatus;
  rejectionReason?: string | null;
}

export default function MerchantBanquetBookings() {
  const [bookings, setBookings] = useState<BanquetBookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedBooking, setSelectedBooking] = useState<BanquetBookingRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<BanquetBookingRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState<string | null>(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, cancelled: 0 });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const notify = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/merchant/banquet-bookings', {
        params: { limit: 100, status: statusFilter !== 'All' ? statusFilter : undefined },
      });
      setBookings(response.data.bookings || []);
      setStats(response.data.stats || { pending: 0, approved: 0, rejected: 0, cancelled: 0 });
    } catch (error) {
      console.error('Error fetching banquet bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleApprove = async (booking: BanquetBookingRow) => {
    setActing(booking._id);
    try {
      await axios.put(`/api/merchant/banquet-bookings/${booking._id}/approve`);
      notify('Booking approved');
      fetchBookings();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not approve this booking', 'error');
    } finally {
      setActing(null);
    }
  };

  const openReject = (booking: BanquetBookingRow) => {
    setRejectTarget(booking);
    setRejectReason('');
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setActing(rejectTarget._id);
    try {
      await axios.put(`/api/merchant/banquet-bookings/${rejectTarget._id}/reject`, { reason: rejectReason.trim() });
      notify('Booking rejected');
      setRejectTarget(null);
      fetchBookings();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not reject this booking', 'error');
    } finally {
      setActing(null);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Banquet Bookings</Typography>
        <Typography variant="body2" color="text.secondary">
          Review and respond to booking requests for your banquet halls.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Pending</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.pending}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Approved</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.approved}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Rejected</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{stats.rejected}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Cancelled</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.secondary' }}>{stats.cancelled}</Typography>
        </Paper>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              {['All', 'pending', 'approved', 'rejected', 'cancelled'].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt === 'All' ? 'All' : statusMeta(opt).label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      <Card>
        {loading ? (
          <Box sx={{ py: 5 }}><LoadingAnimation /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Hall</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Event Date</strong></TableCell>
                  <TableCell><strong>Time Slot</strong></TableCell>
                  <TableCell><strong>Guests</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No booking requests found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {bookings.map((booking) => (
                  <TableRow key={booking._id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography sx={{ fontWeight: 600 }}>{booking.banquetHall?.name || '—'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>{booking.customer?.name || booking.contactName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{booking.contactPhone}</Typography>
                    </TableCell>
                    <TableCell>{new Date(booking.eventDate).toLocaleDateString()}</TableCell>
                    <TableCell>{booking.timeSlot}</TableCell>
                    <TableCell>{booking.guestCount}</TableCell>
                    <TableCell><RialSymbol /> {booking.totalAmount?.toFixed(0)}</TableCell>
                    <TableCell>
                      <Chip label={statusMeta(booking.status).label} color={statusMeta(booking.status).color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => { setSelectedBooking(booking); setDialogOpen(true); }}>
                          View
                        </Button>
                        {booking.status === 'pending' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={acting === booking._id}
                              onClick={() => handleApprove(booking)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={acting === booking._id}
                              onClick={() => openReject(booking)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Booking Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedBooking?.banquetHall?.name || 'Booking Details'}</Typography>
            <Chip label={statusMeta(selectedBooking?.status).label} color={statusMeta(selectedBooking?.status).color} size="small" />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Customer</Typography>
              <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.customer?.name || selectedBooking?.contactName || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedBooking?.customer?.email || ''} {selectedBooking?.contactPhone ? `· ${selectedBooking.contactPhone}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">Event Date</Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {selectedBooking?.eventDate ? new Date(selectedBooking.eventDate).toLocaleDateString() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Time Slot</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.timeSlot}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Guests</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.guestCount}</Typography>
              </Box>
            </Stack>
            <Box>
              <Typography variant="body2" color="text.secondary">Event Type</Typography>
              <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.eventType || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              <Typography sx={{ fontWeight: 600 }}><RialSymbol /> {selectedBooking?.totalAmount?.toFixed(0)}</Typography>
            </Box>
            {selectedBooking?.specialRequests && (
              <Box>
                <Typography variant="body2" color="text.secondary">Special Requests</Typography>
                <Paper sx={{ p: 2, bgcolor: 'action.hover', mt: 1 }}>
                  <Typography variant="body2">{selectedBooking.specialRequests}</Typography>
                </Paper>
              </Box>
            )}
            {selectedBooking?.status === 'rejected' && selectedBooking?.rejectionReason && (
              <Box>
                <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                <Typography variant="body2" color="error.main">{selectedBooking.rejectionReason}</Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Booking</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Let the customer know why this request for "{rejectTarget?.banquetHall?.name}" can't be fulfilled.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmReject} disabled={acting === rejectTarget?._id}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
